import { Dispatch } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
  Button,
  MultiSelectDropdown,
  InlineFormInput,
  TimezoneMultiPicker,
  FormsInputDropdown,
} from '@platform/components';
import { useApplicationLocalStorage } from '../../../../application/application-hook';
import { ProfessionalApplicationStatusEnum } from '@platform/client/schemas';
import { ClientCohortSelects } from '../ClientCohortSelects';

export type ProUserFilters = {
  corporateClient: string[] | null;
  cohort: string[] | null;
  applicationStatus: string[] | null;
  timezone: string[] | null;
  matchStatus: string | null;
  maxIntroductions: string | null;
  requestedCohortNotifications: string | null;
};

export const ProUserSearchForm = (props: {
  setFilters: Dispatch<React.SetStateAction<ProUserFilters | undefined>>;
}) => {
  const { updateApplicationData, getApplicationData, clearApplicationData } =
    useApplicationLocalStorage<ProUserFilters>('pro-search');
  const { setValue, handleSubmit, register, watch, control, formState, reset } =
    useForm<ProUserFilters>({
      values: { ...getApplicationData() },
    });

  const clearFilters = () => {
    reset({
      corporateClient: null,
      cohort: null,
      applicationStatus: null,
      timezone: null,
      matchStatus: null,
      maxIntroductions: null,
      requestedCohortNotifications: null,
    });
    clearApplicationData();
    props.setFilters(undefined);
  };

  const onSubmit: SubmitHandler<ProUserFilters> = (data) => {
    updateApplicationData(data);
    props.setFilters(data);
  };

  return (
    <div className='min-w-full flex flex-col gap-4 items-center'>
      <h1>Professional User Search</h1>
      <form className='min-w-full md:min-w-1/2 flex flex-col gap-1'>
        <ClientCohortSelects
          watch={watch}
          register={register}
          setValue={setValue}
          clientField='corporateClient'
          cohortField='cohort'
        />
        <MultiSelectDropdown
          register={() => register('applicationStatus')}
          label='Application status'
          options={[
            ...Object.values(ProfessionalApplicationStatusEnum).map((status) => ({
              label: status,
            })),
            { label: 'application incomplete' },
          ].sort((a, b) => a.label.localeCompare(b.label))}
          onSelectionChange={(items) => setValue('applicationStatus', items)}
          selectedItems={watch('applicationStatus') || []}
          placeholder='All'
        />
        <TimezoneMultiPicker
          placeholder='All'
          timezone={watch('timezone') ?? undefined}
          updateTimezone={(timezone) => setValue('timezone', timezone)}
          register={() => register('timezone')}
          filterable={true}
        />
        <FormsInputDropdown
          name='matchStatus'
          control={control}
          type='default'
          label='Match status'
          items={[
            { text: 'has matched', textValue: 'matched' },
            { text: 'has not matched', textValue: 'not matched' },
          ]}
          placeholder='All'
        />
        <InlineFormInput
          label='Max introductions'
          inputType='number'
          placeholder='Enter number of introductions'
          register={() => register('maxIntroductions')}
          errors={formState.errors.maxIntroductions}
        />
        <FormsInputDropdown
          name='requestedCohortNotifications'
          type='default'
          label='Requested cohort notifications'
          control={control}
          items={[
            { text: 'yes', textValue: 'yes' },
            { text: 'no', textValue: 'no' },
          ]}
          placeholder='All'
        />
      </form>
      <div className='flex justify-between gap-8 items-center mx-2'>
        <Button
          size='sm'
          variant='tertiary-danger'
          onClick={() => clearFilters()}
          additionalClassnames='w-fit'
        >
          clear filters
        </Button>
        <Button
          size='sm'
          variant='primary'
          onClick={handleSubmit(onSubmit)}
          additionalClassnames='w-fit'
        >
          Preview list
        </Button>
      </div>
    </div>
  );
};
